import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'
import { RouteErrorPage } from '@/components/shared/RouteErrorPage'
import { useAuth } from '@/contexts/AuthContext'
import LoginPage from '@/features/auth/LoginPage'
import SignupPage from '@/features/auth/SignupPage'
import SignupSuccessPage from '@/features/auth/SignupSuccessPage'
import AcceptInvitePage from '@/features/auth/AcceptInvitePage'
import ForgotPasswordPage from '@/features/auth/ForgotPasswordPage'
import ResetPasswordPage from '@/features/auth/ResetPasswordPage'
import PreviewEntryPage from '@/features/portal/preview/PreviewEntryPage'
import AdminLayout from '@/components/shared/AdminLayout'
import PortalLayout from '@/components/shared/PortalLayout'
import { DataProcessingTermsPage, PhotoGuidePage } from '@/features/legal/LegalDocumentPage'
import PhotoNoticePage from '@/features/legal/PhotoNoticePage'
import LegalDocumentEditorPage from '@/features/superadmin/legal/LegalDocumentEditorPage'
import { RequirePortalFeature } from '@/components/shared/RequirePortalFeature'
import SuperAdminLayout from '@/components/shared/SuperAdminLayout'
import TenantsListPage from '@/features/superadmin/tenants/TenantsListPage'
import TenantDetailPage from '@/features/superadmin/tenants/TenantDetailPage'
import SystemTemplatesPage from '@/features/superadmin/system-templates/SystemTemplatesPage'
import EmailTemplatesListPage from '@/features/superadmin/email-templates/EmailTemplatesListPage'
import EmailTemplateEditorPage from '@/features/superadmin/email-templates/EmailTemplateEditorPage'
import LegalDocumentsPage from '@/features/superadmin/legal/LegalDocumentsPage'
import SupportPage from '@/features/superadmin/support/SupportPage'
import SitesPage from '@/features/superadmin/sites/SitesPage'
import SiteDetailPage from '@/features/superadmin/sites/SiteDetailPage'
import WebsitePage from '@/features/website/WebsitePage'
import MonitoringPage from '@/features/superadmin/monitoring/MonitoringPage'
import ChangelogPage from '@/features/superadmin/changelog/ChangelogPage'
import ReleaseNotesPage from '@/features/superadmin/release-notes/ReleaseNotesPage'
import ReleaseNoteEditorPage from '@/features/superadmin/release-notes/ReleaseNoteEditorPage'
import ClientsListPage from '@/features/admin/clients/ClientsListPage'
import ClientDetailPage from '@/features/admin/clients/ClientDetailPage'
import TrainingRecordsPage from '@/features/admin/training/TrainingRecordsPage'
import CoursesPage from '@/features/admin/courses/CoursesPage'
import CertificateTemplatesPage from '@/features/admin/certificates/CertificateTemplatesPage'
import InvoicesListPage from '@/features/admin/invoices/InvoicesListPage'
import InvoicePage from '@/features/admin/invoices/InvoicePage'
import PaymentsPage from '@/features/admin/payments/PaymentsPage'
import BookingsListPage from '@/features/admin/bookings/BookingsListPage'
import BookingDetailPage from '@/features/admin/bookings/BookingDetailPage'
import TrainersPage from '@/features/admin/trainers/TrainersPage'
import TrainerDetailPage from '@/features/admin/trainers/TrainerDetailPage'
import CalendarPage from '@/features/admin/calendar/CalendarPage'
import MyCompanyPage from '@/features/admin/company/MyCompanyPage'
import AdminSupportPage from '@/features/admin/support/SupportPage'
import VenuesPage from '@/features/admin/venues/VenuesPage'
import TrainerBookingRegisterPage from '@/features/trainer/TrainerBookingRegisterPage'
import TrainerSessionRegisterPage from '@/features/trainer/TrainerSessionRegisterPage'
import AccountSettingsPage from '@/features/admin/account/AccountSettingsPage'
import CancelSubscriptionPage from '@/features/admin/account/CancelSubscriptionPage'
import CancellationsPage from '@/features/superadmin/cancellations/CancellationsPage'
import ComplianceDashboardPage from '@/features/admin/reports/ComplianceDashboardPage'
import TeamDashboard from '@/features/portal/dashboard/TeamDashboard'
import StaffTrainingView from '@/features/portal/team/StaffTrainingView'
import OverviewPage from '@/features/portal/overview/OverviewPage'
import CompliancePage from '@/features/portal/compliance/CompliancePage'
import BookingsPage from '@/features/portal/bookings/BookingsPage'
import PortalSessionsPage from '@/features/portal/sessions/SessionsPage'
import InvoicesPage from '@/features/portal/invoices/InvoicesPage'
import PortalInvoicePage from '@/features/portal/invoices/PortalInvoicePage'
import ProfilePage from '@/features/portal/profile/ProfilePage'
import CourseDetailPage from '@/features/admin/courses/CourseDetailPage'
import TrainerLayout from '@/components/shared/TrainerLayout'
import TrainerOverviewPage from '@/features/trainer/TrainerOverviewPage'
import TrainerSchedulePage from '@/features/trainer/TrainerSchedulePage'
import TrainerCalendarPage from '@/features/trainer/TrainerCalendarPage'
import TrainerCoursesPage from '@/features/trainer/TrainerCoursesPage'
import TrainerCourseDetailPage from '@/features/trainer/TrainerCourseDetailPage'
import TrainerFeedbackPage from '@/features/trainer/TrainerFeedbackPage'
import FeedbackPage from '@/features/admin/feedback/FeedbackPage'
import AnalyticsPage from '@/features/admin/analytics/AnalyticsPage'
import SessionsPage from '@/features/admin/sessions/SessionsPage'
import SessionDetailPage from '@/features/admin/sessions/SessionDetailPage'
import AttendeesListPage from '@/features/admin/attendees/AttendeesListPage'
import AttendeeDetailPage from '@/features/admin/attendees/AttendeeDetailPage'
import PublicSessionsPage from '@/features/public/sessions/PublicSessionsPage'
import PublicSessionDetailPage from '@/features/public/sessions/PublicSessionDetailPage'
import PublicBookingViewPage from '@/features/public/sessions/PublicBookingViewPage'
import EmbedSessionsPage from '@/features/public/sessions/EmbedSessionsPage'
import EmbedCoursesPage from '@/features/public/courses/EmbedCoursesPage'
import EmbedCoursePage from '@/features/public/courses/EmbedCoursePage'
import PublicCoursesPage from '@/features/public/courses/PublicCoursesPage'
import PublicCoursePage from '@/features/public/courses/PublicCoursePage'
import VerifyPage from '@/features/verify/VerifyPage'
import PublicSignInPage from '@/features/attendance/PublicSignInPage'
import SignInDisplayPage from '@/features/attendance/SignInDisplayPage'
import AttendeeLayout from '@/components/shared/AttendeeLayout'
import AttendeeRequestLinkPage from '@/features/attendee/AttendeeRequestLinkPage'
import AttendeeMagicLinkPage from '@/features/attendee/AttendeeMagicLinkPage'
import PortalMagicLinkVerifyPage from '@/features/auth/PortalMagicLinkVerifyPage'
import AttendeeOverviewPage from '@/features/attendee/AttendeeOverviewPage'
import AttendeeCertificatesPage from '@/features/attendee/AttendeeCertificatesPage'
import AttendeeProfilePage from '@/features/attendee/AttendeeProfilePage'
import { attendeeAuth } from '@/api/attendee'

function RequireAuth({ roles }: { roles?: string[] }) {
  const { user } = useAuth()
  if (!user) return <Navigate to="/login" replace />
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />
  return <Outlet />
}

function RequireAttendee() {
  if (!attendeeAuth.isAuthed()) {
    const slug = attendeeAuth.slug()
    return <Navigate to={slug ? `/attendee/${slug}/login` : '/'} replace />
  }
  return <Outlet />
}

function RootRedirect() {
  const { user } = useAuth()
  if (!user) return <Navigate to="/login" replace />
  if (user.role === 'SuperAdmin') return <Navigate to="/superadmin/tenants" replace />
  if (user.role === 'TenantAdmin') return <Navigate to="/admin/overview" replace />
  if (user.role === 'Trainer') return <Navigate to="/trainer/overview" replace />
  return <Navigate to="/portal/overview" replace />
}

export const router = createBrowserRouter([
  { path: '/', element: <RootRedirect />, errorElement: <RouteErrorPage /> },
  { path: '/login', element: <LoginPage />, errorElement: <RouteErrorPage /> },
  { path: '/signup', element: <SignupPage />, errorElement: <RouteErrorPage /> },
  { path: '/signup/success', element: <SignupSuccessPage />, errorElement: <RouteErrorPage /> },
  { path: '*', element: <RouteErrorPage /> },
  { path: '/invite/accept', element: <AcceptInvitePage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/portal/preview', element: <PreviewEntryPage /> },
  { path: '/verify/:certificateId', element: <VerifyPage /> },
  { path: '/signin/:code', element: <PublicSignInPage /> },
  { path: '/signin-display/:code', element: <SignInDisplayPage /> },
  { path: '/legal/data-processing', element: <DataProcessingTermsPage /> },
  { path: '/legal/photo-guide', element: <PhotoGuidePage /> },
  { path: '/privacy/photos/:slug', element: <PhotoNoticePage /> },
  { path: '/sessions/:slug', element: <PublicSessionsPage /> },
  { path: '/sessions/:slug/:sessionId', element: <PublicSessionDetailPage /> },
  { path: '/sessions/:slug/bookings/:bookingId', element: <PublicBookingViewPage /> },
  { path: '/embed/:slug', element: <EmbedSessionsPage /> },
  { path: '/embed/courses/:slug', element: <EmbedCoursesPage /> },
  { path: '/embed/course/:slug/:courseSlug', element: <EmbedCoursePage /> },
  { path: '/courses/:slug', element: <PublicCoursesPage /> },
  { path: '/courses/:slug/:courseSlug', element: <PublicCoursePage /> },
  { path: '/attendee/:slug/login', element: <AttendeeRequestLinkPage /> },
  { path: '/attendee/verify', element: <AttendeeMagicLinkPage /> },
  { path: '/portal/magic-link/verify', element: <PortalMagicLinkVerifyPage /> },
  {
    element: <RequireAttendee />,
    children: [{
      element: <AttendeeLayout />,
      children: [
        { path: '/attendee/overview', element: <AttendeeOverviewPage /> },
        { path: '/attendee/certificates', element: <AttendeeCertificatesPage /> },
        { path: '/attendee/profile', element: <AttendeeProfilePage /> },
      ],
    }],
  },
  { path: '/preview-expired', element: (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <p className="text-lg font-medium text-gray-700 mb-2">Preview session expired</p>
        <p className="text-sm text-gray-500">Close this tab and click <strong>View client site</strong> again from the admin panel.</p>
      </div>
    </div>
  )},
  {
    element: <RequireAuth roles={['TenantAdmin']} />,
    children: [{
      element: <AdminLayout />,
      children: [
        { path: '/admin/clients', element: <ClientsListPage /> },
        { path: '/admin/clients/:id', element: <ClientDetailPage /> },
        { path: '/admin/clients/:clientId/staff/:staffId/training', element: <TrainingRecordsPage /> },
        { path: '/admin/courses', element: <CoursesPage /> },
        { path: '/admin/courses/:id', element: <CourseDetailPage /> },
        { path: '/admin/certificate-templates', element: <CertificateTemplatesPage /> },
        { path: '/admin/bookings', element: <BookingsListPage /> },
        { path: '/admin/bookings/:id', element: <BookingDetailPage /> },
        { path: '/admin/trainers', element: <TrainersPage /> },
        { path: '/admin/trainers/:id', element: <TrainerDetailPage /> },
        { path: '/admin/calendar', element: <CalendarPage /> },
        { path: '/admin/invoices', element: <InvoicesListPage /> },
        { path: '/admin/invoices/:id', element: <InvoicePage /> },
        { path: '/admin/payments', element: <PaymentsPage /> },
        { path: '/admin/company', element: <MyCompanyPage /> },
        { path: '/admin/support', element: <AdminSupportPage /> },
        { path: '/admin/venues', element: <VenuesPage /> },
        { path: '/admin/website', element: <WebsitePage /> },
        { path: '/admin/overview', element: <ComplianceDashboardPage /> },
        { path: '/admin/account', element: <AccountSettingsPage /> },
        { path: '/admin/account/cancel', element: <CancelSubscriptionPage /> },
        { path: '/admin/feedback', element: <FeedbackPage /> },
        { path: '/admin/analytics', element: <AnalyticsPage /> },
        { path: '/admin/sessions', element: <SessionsPage /> },
        { path: '/admin/sessions/:id', element: <SessionDetailPage /> },
        { path: '/admin/attendees', element: <AttendeesListPage /> },
        { path: '/admin/attendees/:id', element: <AttendeeDetailPage /> },
        // Admin-as-trainer routes — same pages as /trainer/*, but inside admin layout
        { path: '/admin/trainer/overview', element: <TrainerOverviewPage basePath="/admin/trainer" /> },
        { path: '/admin/trainer/schedule', element: <TrainerSchedulePage basePath="/admin/trainer" /> },
        { path: '/admin/trainer/calendar', element: <TrainerCalendarPage basePath="/admin/trainer" /> },
        { path: '/admin/trainer/bookings/:bookingId', element: <TrainerBookingRegisterPage /> },
        { path: '/admin/trainer/sessions/:sessionId', element: <TrainerSessionRegisterPage /> },
        { path: '/admin/trainer/courses', element: <TrainerCoursesPage basePath="/admin/trainer" /> },
        { path: '/admin/trainer/courses/:courseId', element: <TrainerCourseDetailPage /> },
        { path: '/admin/trainer/feedback', element: <TrainerFeedbackPage /> },
      ],
    }],
  },
  {
    element: <RequireAuth roles={['TenantAdmin']} />,
    children: [{
      path: '/admin/website/edit',
      lazy: async () => ({ Component: (await import('@/features/website/EditorRoutes')).OwnEditorRoute }),
    }],
  },
  {
    element: <RequireAuth roles={['SuperAdmin']} />,
    children: [{
      path: '/superadmin/sites/:tenantId/edit',
      lazy: async () => ({ Component: (await import('@/features/website/EditorRoutes')).StaffEditorRoute }),
    }],
  },
  {
    element: <RequireAuth roles={['ClientManager']} />,
    children: [{
      element: <PortalLayout />,
      children: [
        { path: '/portal/overview', element: <OverviewPage /> },
        { path: '/portal/team', element: <TeamDashboard /> },
        { path: '/portal/team/:staffId', element: <StaffTrainingView /> },
        { path: '/portal/compliance', element: <CompliancePage /> },
        { path: '/portal/bookings', element: <BookingsPage /> },
        { path: '/portal/sessions', element: <RequirePortalFeature feature="bookSessions"><PortalSessionsPage /></RequirePortalFeature> },
        { path: '/portal/invoices', element: <RequirePortalFeature feature="invoices"><InvoicesPage /></RequirePortalFeature> },
        { path: '/portal/invoices/:id', element: <RequirePortalFeature feature="invoices"><PortalInvoicePage /></RequirePortalFeature> },
        { path: '/portal/profile', element: <ProfilePage /> },
        // Legacy redirects
        { path: '/portal/dashboard', element: <Navigate to="/portal/overview" replace /> },
      ],
    }],
  },
  {
    element: <RequireAuth roles={['Trainer']} />,
    children: [{
      element: <TrainerLayout />,
      children: [
        { path: '/trainer/overview', element: <TrainerOverviewPage /> },
        { path: '/trainer/schedule', element: <TrainerSchedulePage /> },
        { path: '/trainer/calendar', element: <TrainerCalendarPage /> },
        { path: '/trainer/bookings/:bookingId', element: <TrainerBookingRegisterPage /> },
        { path: '/trainer/sessions/:sessionId', element: <TrainerSessionRegisterPage /> },
        { path: '/trainer/courses', element: <TrainerCoursesPage /> },
        { path: '/trainer/courses/:courseId', element: <TrainerCourseDetailPage /> },
        { path: '/trainer/feedback', element: <TrainerFeedbackPage /> },
      ],
    }],
  },
  {
    element: <RequireAuth roles={['SuperAdmin']} />,
    children: [{
      element: <SuperAdminLayout />,
      children: [
        { path: '/superadmin/tenants', element: <TenantsListPage /> },
        { path: '/superadmin/tenants/:id', element: <TenantDetailPage /> },
        { path: '/superadmin/system-templates', element: <SystemTemplatesPage /> },
        { path: '/superadmin/email-templates', element: <EmailTemplatesListPage /> },
        { path: '/superadmin/email-templates/:key', element: <EmailTemplateEditorPage /> },
        { path: '/superadmin/legal', element: <LegalDocumentsPage /> },
        { path: '/superadmin/cancellations', element: <CancellationsPage /> },
        { path: '/superadmin/legal/:key', element: <LegalDocumentEditorPage /> },
        { path: '/superadmin/support', element: <SupportPage /> },
        { path: '/superadmin/sites', element: <SitesPage /> },
        { path: '/superadmin/sites/:tenantId', element: <SiteDetailPage /> },
        { path: '/superadmin/monitoring', element: <MonitoringPage /> },
        { path: '/superadmin/changelog', element: <ChangelogPage /> },
        { path: '/superadmin/release-notes', element: <ReleaseNotesPage /> },
        { path: '/superadmin/release-notes/:id', element: <ReleaseNoteEditorPage /> },
      ],
    }],
  },
])
